import { Badge } from '@/components/ui/badge';
import { formatHours, percentOf, SERIES } from '../charts/chart-theme';
import type { CohortRow, CohortSegment } from '../charts/cohort-bar-chart';
import type { WidgetColumn } from '../widget-table';
import { type Person, TENURE_SPLIT } from './fixtures';

/** Segments every cohort bar is split by — the three ways an hour can be spent. */
export const PEOPLE_COHORT_SEGMENTS: CohortSegment[] = [
  { key: 'billable', label: 'Billable', color: SERIES.billable },
  { key: 'nonBillable', label: 'Non-billable', color: SERIES.nonBillable },
  { key: 'internal', label: 'Internal (msCFO)', color: SERIES.internal },
];

function aggregate(people: Person[]) {
  return people.reduce(
    (a, p) => ({
      count: a.count + 1,
      available: a.available + p.available,
      committed: a.committed + p.committed,
      billable: a.billable + p.billable,
      nonBillable: a.nonBillable + p.nonBillable,
      internal: a.internal + p.internal,
    }),
    { count: 0, available: 0, committed: 0, billable: 0, nonBillable: 0, internal: 0 },
  );
}

function cohortRow(label: string, people: Person[]): CohortRow {
  const t = aggregate(people);
  return {
    cohort: label,
    values: { billable: t.billable, nonBillable: t.nonBillable, internal: t.internal },
    annotation: `${t.count} ppl`,
    footnote: `${label}: ${t.count} ppl · ${percentOf(t.committed, t.available)}% utilized · ${percentOf(t.billable, t.available)}% billable`,
  };
}

/**
 * The two cohort splits every people drilldown offers: employment type, and
 * tenure either side of the {@link TENURE_SPLIT} boundary. Both were asked for
 * by name — "drill down from the pool into FTE vs Consultants, tenure ≶ 6 months".
 */
export function peopleCohorts(people: Person[]) {
  return [
    {
      title: 'FTE vs Consultant',
      rows: [
        cohortRow(
          'FTE',
          people.filter((p) => p.payType === 'FTE'),
        ),
        cohortRow(
          'Consultant',
          people.filter((p) => p.payType === 'Consultant'),
        ),
      ],
    },
    {
      title: `Tenure — under / over ${TENURE_SPLIT} months`,
      rows: [
        cohortRow(
          `< ${TENURE_SPLIT} mo`,
          people.filter((p) => p.tenure < TENURE_SPLIT),
        ),
        cohortRow(
          `≥ ${TENURE_SPLIT} mo`,
          people.filter((p) => p.tenure >= TENURE_SPLIT),
        ),
      ],
    },
  ];
}

export function peopleSearchText(p: Person): string {
  return `${p.name} ${p.location ?? ''} ${p.designatedRole} ${p.workedRole} ${p.manager} ${p.payType}`;
}

/** Columns for the drilldown's table view. */
export const PEOPLE_COLUMNS: WidgetColumn<Person>[] = [
  { key: 'name', header: 'Employee', render: (p) => p.name, sortValue: (p) => p.name },
  {
    key: 'location',
    header: 'Location',
    render: (p) => p.location ?? '—',
    sortValue: (p) => p.location ?? '',
  },
  {
    key: 'designatedRole',
    header: 'Designated',
    render: (p) => <Badge variant="outline">{p.designatedRole}</Badge>,
    sortValue: (p) => p.designatedRole,
  },
  {
    key: 'workedRole',
    header: 'Worked',
    render: (p) => (
      <Badge variant={p.workedRole === p.designatedRole ? 'outline' : 'destructive'}>
        {p.workedRole}
      </Badge>
    ),
    sortValue: (p) => p.workedRole,
  },
  {
    key: 'payType',
    header: 'Pay type',
    render: (p) => <Badge variant="secondary">{p.payType}</Badge>,
    sortValue: (p) => p.payType,
  },
  { key: 'pod', header: 'POD', render: (p) => p.pod, sortValue: (p) => p.pod },
  {
    key: 'tenure',
    header: 'Tenure',
    numeric: true,
    render: (p) => `${p.tenure}m`,
    sortValue: (p) => p.tenure,
  },
  {
    key: 'available',
    header: 'Available',
    numeric: true,
    render: (p) => formatHours(p.available),
    sortValue: (p) => p.available,
  },
  {
    key: 'committed',
    header: 'Committed',
    numeric: true,
    render: (p) => formatHours(p.committed),
    sortValue: (p) => p.committed,
  },
  {
    key: 'billable',
    header: 'Billable',
    numeric: true,
    render: (p) => formatHours(p.billable),
    sortValue: (p) => p.billable,
  },
  {
    key: 'nonBillable',
    header: 'Non-bill',
    numeric: true,
    render: (p) => formatHours(p.nonBillable),
    sortValue: (p) => p.nonBillable,
  },
  {
    key: 'internal',
    header: 'msCFO',
    numeric: true,
    render: (p) => formatHours(p.internal),
    sortValue: (p) => p.internal,
  },
  {
    key: 'mismatchHours',
    header: 'Off-level',
    numeric: true,
    render: (p) => (p.mismatchHours ? formatHours(p.mismatchHours) : '—'),
    sortValue: (p) => p.mismatchHours,
  },
  {
    key: 'utilization',
    header: 'Util %',
    numeric: true,
    render: (p) => <b>{p.utilization}%</b>,
    sortValue: (p) => p.utilization,
  },
];
